// Core imports
import GraniteError from 'granite-admin/utils/granite-error';
import GraniteAPIClient from 'granite-admin/utils/granite-client';
// Application imports
import Organisation from 'granite-admin/organisations/entities/organisation';
import Role from 'granite-admin/organisations/entities/role';
import Permission from 'granite-admin/organisations/entities/permission';
import EmployeeEntity from 'granite-admin/organisations/entities/employee';
import PanelPermission from 'granite-admin/organisations/entities/PanelPermission';

/**
 *  Create Organisation api call implementation.
 *
 * @param {Object} organisation data.
 * @param {string} organisation.name - Name of organisation
 * @param {string} organisation.description
 * @param {object} organisation.address
 *
 * @returns {string} 'success' as string message.
 * @throws {GraniteError} if api call fails
 */

const url = process.env.REACT_APP_ORGANISATION_BASE_URL;
async function createOrganizationOnListing(values) {
  const payload = {
    ...values
  };
  const response = await GraniteAPIClient(url).post('/organisations/create/', payload);
  return new Organisation(response.data);
}
async function createOrganisation(values) {
  const payload = {
    ...values
  };
  try {
    const response = await GraniteAPIClient(url).post('/organisations/register/', payload);
    return new Organisation(response.data);
  } catch (error) {
    throw error;
  }
}
async function myOrganisations() {
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/organisations/');
    const organisations = data.results.map(organisation => new Organisation(organisation)) || [];
    return organisations;
  } catch (error) {
    throw new GraniteError(error);
  }
}
async function getPermissions(params) {
  const payload = {
    params: {
      ...params,
      page_size: 499
    }
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/permissions/', payload);
    const permissions = data ? data.results.map(item => new Permission({
      ...item
    })) : [];
    return {
      permissions,
      count: data.count
    };
  } catch (error) {
    console.log(error);
    const errorMessage = error.response.data.message;
    throw new GraniteError(errorMessage);
  }
}
async function getAdminPermissions(params) {
  const payload = {
    params: {
      ...params,
      page_size: 499
    }
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/permissions/business-admin/', payload);
    const permissions = data ? data.results.map(item => new Permission({
      ...item
    })) : [];
    return {
      permissions,
      count: data.count
    };
  } catch (error) {
    console.log(error);
    const errorMessage = error.response.data.message;
    throw new GraniteError(errorMessage);
  }
}
async function getRoles() {
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/organisation/roles/', {
      params: {
        page_size: 499,
        ordering: 'name'
      }
    });
    const roles = data ? data.results.map(item => new Role({
      ...item
    })) : [];
    return {
      roles,
      count: data.count
    };
  } catch (error) {
    console.log(error);
    const errorMessage = error.response.data.message;
    throw new GraniteError(errorMessage);
  }
}
async function getRolesByOrganisation(organisation) {
  const headers = {
    organisation: organisation.pk
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url, headers).get('/organisation/roles/', {
      params: {
        page_size: 499
      }
    });
    const roles = data ? data.results.map(item => new Role({
      ...item
    })) : [];
    return {
      roles,
      count: data.count
    };
  } catch (error) {
    console.log(error);
    const errorMessage = error.response.data.message;
    throw new GraniteError(errorMessage);
  }
}

/**
 *  Create Role api call implementation.
 *
 * @param {Object} role data.
 * @param {string} name - Name of role

 * @returns {string} 'success' as string message.
 * @throws {GraniteError} if api call fails
 */

async function createRole(values) {
  let payload = {
    name: values.name,
    permissions: []
  };
  try {
    const response = await GraniteAPIClient(url).post('/organisation/roles/', payload);
    return new Role(response.data);
  } catch (error) {
    throw error;
  }
}

/**
 *  Edit Role api call implementation.
 *
 * @param {Object} role data.
 * @param {string} name - Name of role
 * @param {string} id - ID of role

 * @returns {string} 'success' as string message.
 * @throws {GraniteError} if api call fails
 */

async function editRole(pk, name, permissions, type) {
  let payload = type === 'name' ? {
    name
  } : type === 'permissions' ? {
    name,
    permissions
  } : {};
  try {
    let response = await GraniteAPIClient(url).put(`/organisation/role/${pk}/`, payload);
    return new Role(response.data);
  } catch (error) {
    console.log(error);
    const errorMessage = error.response.data.message;
    throw new GraniteError(errorMessage);
  }
}
async function deleteRole(pk) {
  try {
    return await GraniteAPIClient(url).delete(`/organisation/role/${pk}/`);
  } catch (e) {
    throw new GraniteError(e.errors);
  }
}
async function getSettings(params) {
  try {
    const payload = {
      params: {
        ...params
      }
    };
    const {
      data
    } = await GraniteAPIClient(url).get('/settings/', payload);
    return data.results.map(({
      pk,
      value,
      setting_key
    }) => ({
      pk,
      value,
      name: setting_key
    }));
  } catch (error) {
    throw new GraniteError(error);
  }
}
async function updateOrganisation(values) {
  const payload = {
    ...values
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).put(`/organisations/profile/`, payload);
    return new Organisation(data);
  } catch (error) {
    throw error;
  }
}
async function updateSettings(pk, value) {
  const payload = {
    value
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).put(`/settings/${pk}/`, payload);
    return data;
  } catch (error) {
    throw error;
  }
}
async function searchEmployee(q) {
  const payload = {
    params: {
      search: q
    }
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/employee-search/', payload);
    const list = data.results.map(e => new EmployeeEntity(e));
    return {
      list,
      count: data.count
    };
  } catch (error) {
    throw new GraniteError(error.errors);
  }
}
async function isBusinessAdmin() {
  try {
    await GraniteAPIClient(url).get('/business-admin/profile/');
    return true;
  } catch (error) {
    if (error?.errors?.status === 403) return false;
    throw error;
  }
}
async function getAdminOrganisations(params, signal) {
  try {
    const payload = {
      params,
      signal
    };
    const {
      data
    } = await GraniteAPIClient(url).get('/business-admin/organisations/', payload);
    const organisations = data.results.map(organisation => new Organisation(organisation)) || [];
    return {
      list: organisations,
      count: data.count
    };
  } catch (error) {
    throw error;
  }
}
async function sendInvitationEmail(email) {
  try {
    const payload = {
      email
    };
    await GraniteAPIClient(url).post('/send-email-invite/', payload);
  } catch (error) {
    throw error;
  }
}
async function getPanelPermission(path) {
  const {
    data
  } = await GraniteAPIClient(url).get('/panel-permissions/', {
    params: {
      route_id: path
    }
  });
  const panelPermissions = data.results.map(i => new PanelPermission(i));
  return panelPermissions;
}
async function getEmployeePermissions(orgId) {
  const payload = {
    params: {
      page_size: 499
    },
    headers: {
      organisation: orgId
    }
  };
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/employee/permissions/', payload);
    const permissions = data ? data.results.map(item => new Permission({
      ...item
    })) : [];
    return permissions;
  } catch (error) {
    console.log(error);
    return [];
  }
}
async function getOrganisationDetail(orgId) {
  try {
    const {
      data
    } = await GraniteAPIClient(url).get(`/organisations/profile/`, {
      headers: {
        organisation: orgId
      }
    });
    return new Organisation(data);
  } catch (error) {
    throw new GraniteError(error.errors);
  }
}
async function getAccessToken() {
  try {
    const {
      data
    } = await GraniteAPIClient(url).get(`/employees/employee-token/`);
    return data;
  } catch (error) {
    throw new GraniteError(error.errors);
  }
}
export default {
  createOrganizationOnListing,
  createOrganisation,
  myOrganisations,
  getSettings,
  updateSettings,
  updateOrganisation,
  getRoles,
  getRolesByOrganisation,
  getPermissions,
  getAdminPermissions,
  createRole,
  editRole,
  deleteRole,
  searchEmployee,
  isBusinessAdmin,
  getAdminOrganisations,
  sendInvitationEmail,
  getPanelPermission,
  getEmployeePermissions,
  getOrganisationDetail,
  getAccessToken
};